import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/auth";
import {
    Package,
    Star,
    TrendingUp,
    ExternalLink,
    Sparkles,
    Plus,
    CheckCircle2,
    AlertCircle,
    Truck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

export default function MerchantHome() {
    const [stats, setStats] = useState(null);
    const [onboarding, setOnboarding] = useState(null);
    const [readiness, setReadiness] = useState(null);

    useEffect(() => {
        apiFetch.get("/merchant/stats").then((r) => setStats(r.data)).catch(() => {});
        apiFetch.get("/merchant/onboarding").then((r) => setOnboarding(r.data)).catch(() => {});
        apiFetch.get("/merchant/store/readiness").then((r) => setReadiness(r.data)).catch(() => {});
    }, []);

    if (!stats || !onboarding) {
        return (
            <div className="space-y-4">
                <div className="h-32 rounded-xl bg-zinc-100 animate-pulse" />
                <div className="h-48 rounded-xl bg-zinc-100 animate-pulse" />
            </div>
        );
    }

    const progressPct = (onboarding.progress / onboarding.total) * 100;

    return (
        <div data-testid="merchant-home" className="space-y-8">
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-zinc-900">
                    Hola{stats.store?.name ? "," : ""}{" "}
                    {stats.store?.name || "vendedor"} 👋
                </h1>
                <p className="mt-2 text-sm text-zinc-600">
                    Aquí está el resumen de tu negocio en Tiendify.
                </p>
            </div>

            {!onboarding.complete && (
                <div
                    data-testid="merchant-onboarding-banner"
                    className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-amber-50 p-6 sm:p-8"
                >
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary grid place-items-center text-primary-foreground shrink-0">
                            <Sparkles className="w-5 h-5" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                                Onboarding · {onboarding.progress}/{onboarding.total}
                            </p>
                            <h2 className="mt-1 text-xl font-semibold text-zinc-900">
                                Estás a {onboarding.total - onboarding.progress} pas
                                {onboarding.total - onboarding.progress === 1 ? "o" : "os"}{" "}
                                de tu primera venta
                            </h2>
                            <Progress value={progressPct} className="mt-4 h-1.5" />
                            <Button
                                asChild
                                className="mt-5 bg-primary hover:bg-primary-hover text-primary-foreground"
                            >
                                <Link
                                    to="/dashboard/onboarding"
                                    data-testid="merchant-home-onboarding-cta"
                                >
                                    Continuar onboarding
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard
                    icon={Package}
                    label="Productos"
                    value={stats.products}
                />
                <StatCard
                    icon={TrendingUp}
                    label="Ventas (mock)"
                    value={stats.sales}
                />
                <StatCard
                    icon={Star}
                    label="Calificación"
                    value={stats.rating ? stats.rating.toFixed(1) : "—"}
                    hint={`${stats.reviews || 0} reseñas`}
                />
            </div>

            {readiness && !readiness.ready && (
                <ReadinessCard readiness={readiness} />
            )}

            <div className="grid sm:grid-cols-2 gap-4">
                <Link
                    to="/dashboard/products/new"
                    data-testid="merchant-home-quick-product"
                    className="group rounded-xl border border-zinc-200 bg-white p-5 hover:border-indigo-300 hover:shadow-sm transition-all flex items-start gap-3"
                >
                    <div className="w-10 h-10 rounded-lg bg-primary-subtle grid place-items-center text-primary">
                        <Plus className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-zinc-900 group-hover:text-primary">
                            Subir un producto
                        </h3>
                        <p className="text-xs text-zinc-600 mt-0.5">
                            Foto, precio, variantes y listo.
                        </p>
                    </div>
                </Link>

                {stats.store?.slug && (
                    <a
                        href={`/${stats.store.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="merchant-home-view-store"
                        className="group rounded-xl border border-zinc-200 bg-white p-5 hover:border-indigo-300 hover:shadow-sm transition-all flex items-start gap-3"
                    >
                        <div className="w-10 h-10 rounded-lg bg-amber-50 grid place-items-center text-amber-700">
                            <ExternalLink className="w-5 h-5" aria-hidden="true" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-zinc-900 group-hover:text-primary">
                                Ver mi tienda pública
                            </h3>
                            <p className="text-xs text-zinc-600 mt-0.5">
                                tiendify.io/{stats.store.slug}
                            </p>
                        </div>
                    </a>
                )}
            </div>
        </div>
    );
}

function ReadinessCard({ readiness }) {
    const items = [
        {
            key: "shipping_origin",
            label: "Dirección de origen",
            to: "/dashboard/store",
            cta: "Completar",
            data: readiness.checks.shipping_origin,
            renderMissing: (c) =>
                c.missing?.length ? `Faltan: ${c.missing.join(", ")}.` : c.hint,
        },
        {
            key: "products_weight",
            label: "Productos con peso",
            to: "/dashboard/products",
            cta: "Revisar productos",
            data: readiness.checks.products_weight,
            renderMissing: (c) => {
                if (!c.total) return "Aún no tienes productos publicados.";
                if (c.missing_weight)
                    return `${c.missing_weight} producto(s) no tienen peso. Edítalos para poder vender.`;
                return c.hint;
            },
        },
        {
            key: "payment_method",
            label: "Pagos con tarjeta",
            data: readiness.checks.payment_method,
            renderMissing: (c) => c.hint,
        },
    ];

    return (
        <div
            data-testid="merchant-readiness-card"
            className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 sm:p-6"
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 grid place-items-center text-amber-700 shrink-0">
                    <Truck className="w-5 h-5" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-amber-900">
                        Tu tienda aún no está lista para vender
                    </h3>
                    <p className="mt-1 text-xs text-amber-800/80">
                        Completa estos pasos para que tus compradores puedan
                        pagar y generar guías de envío.
                    </p>
                </div>
            </div>
            <ul className="mt-4 space-y-2">
                {items.map((item) => {
                    const ok = item.data?.ok;
                    return (
                        <li
                            key={item.key}
                            className="flex items-center gap-3 rounded-lg bg-white border border-amber-100 px-3 py-2"
                            data-testid={`readiness-${item.key}`}
                        >
                            {ok ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                            ) : (
                                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-zinc-900">
                                    {item.label}
                                </div>
                                {!ok && (
                                    <div className="text-xs text-zinc-600 mt-0.5">
                                        {item.renderMissing(item.data || {})}
                                    </div>
                                )}
                            </div>
                            {!ok && !item.disabled && (
                                <Link
                                    to={item.to}
                                    className="text-xs font-semibold text-primary hover:underline shrink-0"
                                >
                                    {item.cta}
                                </Link>
                            )}
                            {!ok && item.disabled && (
                                <span className="text-xs text-zinc-400 shrink-0">
                                    {item.cta}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, hint }) {
    return (
        <div className="rounded-xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-700">
                <Icon className="w-4 h-4 text-primary" aria-hidden="true" />
                {label}
            </div>
            <div className="mt-3 text-3xl font-bold text-zinc-900 tabular-nums">{value}</div>
            {hint && <div className="mt-1 text-xs text-zinc-600">{hint}</div>}
        </div>
    );
}
